// frontend/src/components/athleteEditor/AddToReportModal.jsx
// "Add to Report" for the athlete detail page — puts the athlete on the reports
// managed at /lists (app/routers/reports.py), using that page's
// interaction model: each row is a "+ Add" / "✓ In" button that commits on click.
//
// Three constraints from the reports API shape this component:
//  1. Reports are locked to one sport, and the server silently DROPS ineligible
//     athletes (report_eligibility.py) rather than erroring. So we only list
//     reports matching this athlete's sport, and we verify after every write.
//  2. PUT /reports/{id} REPLACES membership wholesale — adding one athlete means
//     sending the report's full existing member list plus this athlete's id.
//     That's why we load each report's members up front.
//  3. PUT doesn't echo included/skipped ids (unlike POST), so the only way to
//     know a write took effect is to re-read the report.
import React, { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import Button from '../Button';

export default function AddToReportModal({
  athleteUserId, athleteName, athleteSportSlug, athleteSportName,
  apiUrl, getConfig, onClose,
}) {
  const [reports, setReports] = useState([]);       // [{id, title, memberIds: number[]}]
  const [loading, setLoading] = useState(true);
  const [pending, setPending] = useState(() => new Set());
  const [newTitle, setNewTitle] = useState('');
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState('');

  // The parent builds getConfig inline, so its identity changes every render;
  // a ref keeps `load` stable instead of re-firing the fetch in a loop.
  const getConfigRef = useRef(getConfig);
  getConfigRef.current = getConfig;

  const uid = Number(athleteUserId);

  // Membership lives in `reports[].memberIds`, so a stale closure would send an
  // out-of-date array to a full-replace PUT and drop other athletes. Reads go
  // through this ref.
  const reportsRef = useRef(reports);
  const setReportsBoth = (next) => { reportsRef.current = next; setReports(next); };

  const fetchMembers = useCallback(async (reportId) => {
    const res = await axios.get(`${apiUrl}/reports/${reportId}`, getConfigRef.current());
    return (res.data?.members || []).map(m => Number(m.user_id ?? m.athlete_user_id)).filter(Boolean);
  }, [apiUrl]);

  const load = useCallback(async () => {
    if (!athleteSportSlug) { setLoading(false); return; }
    try {
      const res = await axios.get(`${apiUrl}/reports`, getConfigRef.current());
      // Only same-sport reports can accept this athlete — anything else would
      // 200 and silently drop them.
      const mine = (res.data || []).filter(r => r.sport === athleteSportSlug);
      const withMembers = await Promise.all(mine.map(async (r) => ({
        id: r.id,
        title: r.title,
        memberIds: await fetchMembers(r.id).catch(() => []),
      })));
      setReportsBoth(withMembers);
      setError('');
    } catch (e) {
      setError(e.response?.data?.detail || 'Could not load your reports.');
    } finally {
      setLoading(false);
    }
  }, [apiUrl, athleteSportSlug, fetchMembers]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    const onKey = e => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const patchReport = (reportId, memberIds) => {
    const next = reportsRef.current.map(r => (r.id === reportId ? { ...r, memberIds } : r));
    setReportsBoth(next);
  };

  const commit = async (reportId, shouldBeMember) => {
    if (pending.has(reportId)) return;
    const report = reportsRef.current.find(r => r.id === reportId);
    if (!report) return;

    const before = report.memberIds;
    const desired = shouldBeMember
      ? [...before.filter(id => id !== uid), uid]
      : before.filter(id => id !== uid);

    patchReport(reportId, desired);            // optimistic
    setPending(prev => new Set(prev).add(reportId));
    setError('');
    try {
      await axios.put(`${apiUrl}/reports/${reportId}`,
        { athlete_user_ids: desired }, getConfigRef.current());

      // PUT reports nothing about drops, so read back the truth.
      const actual = await fetchMembers(reportId);
      patchReport(reportId, actual);
      if (shouldBeMember && !actual.includes(uid)) {
        setError(
          `${athleteName || 'This athlete'} can't be added to "${report.title}" — reports only accept ` +
          `athletes with an active subscription or concierge status in ${athleteSportName || 'this sport'}.`
        );
      }
    } catch (e) {
      patchReport(reportId, before);           // revert this report only
      setError(e.response?.data?.detail || 'Could not update that report. Please try again.');
    } finally {
      setPending(prev => { const p = new Set(prev); p.delete(reportId); return p; });
    }
  };

  // POST echoes included/skipped, so ineligibility is detectable directly here.
  const createReport = async () => {
    const title = newTitle.trim();
    if (!title || creating) return;
    setCreating(true);
    setError('');
    try {
      const res = await axios.post(`${apiUrl}/reports`,
        { title, sport: athleteSportSlug, athlete_user_ids: [uid] }, getConfigRef.current());
      const created = res.data;
      const included = created.included_ids || [];
      setReportsBoth([...reportsRef.current, { id: created.id, title: created.title, memberIds: included }]);
      setNewTitle('');
      if (!included.includes(uid)) {
        setError(
          `Created "${created.title}", but ${athleteName || 'this athlete'} wasn't added — reports only ` +
          `accept athletes with an active subscription or concierge status in ${athleteSportName || 'this sport'}.`
        );
      }
    } catch (e) {
      setError(e.response?.data?.detail || 'Could not create that report.');
    } finally {
      setCreating(false);
    }
  };

  const rowBtn = (isMember, busy) => ({
    minWidth: 78,
    padding: '6px 12px',
    fontSize: 13,
    fontWeight: 600,
    borderRadius: 6,
    cursor: busy ? 'default' : 'pointer',
    border: `1px solid ${isMember ? '#cbd5e1' : '#4D8064'}`,
    background: isMember ? '#f8fafc' : '#4D8064',
    color: isMember ? '#475569' : '#fff',
    opacity: busy ? 0.6 : 1,
    transition: 'all 0.15s',
  });

  return (
    // .modal-panel (not .modal-content) carries the white card — .modal-content
    // lives in styles/index.css, which nothing imports.
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-panel" onClick={e => e.stopPropagation()} style={{ maxWidth: 460 }}>
        <button className="modal-close" onClick={onClose} aria-label="Close">&times;</button>
        <h2 style={{ margin: '0 0 6px', fontSize: 20, fontWeight: 700, color: '#0f172a' }}>Add to Report</h2>
        <p style={{ margin: '0 0 16px', fontSize: 14, color: '#64748b' }}>
          {athleteSportName
            ? <>Showing {athleteSportName} reports. Changes save as you click.</>
            : <>Changes save as you click.</>}
        </p>

        {!athleteSportSlug ? (
          <p style={{ fontSize: 14, color: '#64748b' }}>
            This athlete has no sport set, so they can't be added to a report yet.
          </p>
        ) : loading ? (
          <p style={{ fontSize: 14, color: '#64748b' }}>Loading reports…</p>
        ) : (
          <>
            {reports.length === 0 ? (
              <p style={{ fontSize: 14, color: '#64748b' }}>
                No {athleteSportName} reports yet. Create one below.
              </p>
            ) : (
              <div style={{ maxHeight: 280, overflowY: 'auto', marginBottom: 16 }}>
                {reports.map(r => {
                  const isMember = r.memberIds.includes(uid);
                  const busy = pending.has(r.id);
                  return (
                    <div
                      key={r.id}
                      style={{
                        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                        gap: 12, padding: '10px 0', borderBottom: '1px solid #f1f5f9',
                      }}
                    >
                      <span style={{ minWidth: 0 }}>
                        <span style={{ display: 'block', fontSize: 14, color: '#0f172a', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {r.title}
                        </span>
                        <span style={{ fontSize: 12, color: '#94a3b8' }}>
                          {r.memberIds.length} athlete{r.memberIds.length === 1 ? '' : 's'}
                        </span>
                      </span>
                      <button
                        type="button"
                        style={rowBtn(isMember, busy)}
                        disabled={busy}
                        onClick={() => commit(r.id, !isMember)}
                        title={isMember ? `Remove from ${r.title}` : `Add to ${r.title}`}
                        aria-label={isMember ? `Remove from ${r.title}` : `Add to ${r.title}`}
                      >
                        {busy ? '…' : isMember ? '✓ In' : '+ Add'}
                      </button>
                    </div>
                  );
                })}
              </div>
            )}

            <div style={{ display: 'flex', gap: 8, marginBottom: 14 }}>
              <input
                type="text"
                className="filter-input"
                placeholder="New report title…"
                value={newTitle}
                maxLength={120}
                onChange={e => setNewTitle(e.target.value)}
                onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); createReport(); } }}
                style={{ flex: 1 }}
              />
              <Button variant="secondary" onClick={createReport} disabled={!newTitle.trim() || creating}>
                {creating ? 'Adding…' : 'Create'}
              </Button>
            </div>

            {error && <p style={{ color: '#dc2626', fontSize: 13, margin: '0 0 12px' }}>{error}</p>}

            <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
              <Button variant="primary" onClick={onClose}>Done</Button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
